import { css, html, LitElement } from 'lit'
import { property, state } from 'lit/decorators.js'
import { safeCustomElement } from '@/utilities/custom-element'
@safeCustomElement('mdc-header-search')
export class MdcHeaderSearch extends LitElement {
  @property({ type: String }) placeholder = 'Search'
  @property({ type: String }) value = ''
  @property({ type: Boolean }) hovered = false
  @property({ type: Boolean }) focused = false

  @state() private internalValue = ''
  @state() private isFocused = false
  @state() private isHovered = false

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
			--mdc-header-search__icon-size: 16px;
			--mdc-header-search-border-color: var(--color-functional-gray-200, #e5e5e5);
			--mdc-header-search-border-hover-color: var(--color-functional-gray-400, #999999);
			--mdc-header-search-background: var(--color-functional-gray-white, #ffffff);
			--mdc-header-search-focus-color: var(--color-functional-blue-300, #0095f6);
			--mdc-header-search-input-font-family: var(--typography-font-family-sans);
			--mdc-header-search-input-font-size: var(--text-087-and-half, 0.875rem);
			--mdc-header-search-input-color: var(--color-functional-gray-black, #111111);
			--mdc-header-search-placeholder-color: var(--color-functional-gray-400, #999999);
		}

		.mdc-header-search {
			display: flex;
			position: relative;
			border: 1px solid var(--mdc-header-search-border-color);
			border-radius: 0.5rem;
			background-color: var(--mdc-header-search-background);
			transition: border-color 0.1s ease-in-out, box-shadow 0.1s ease-in-out;
		}

		.mdc-header-search--hovered,
		.mdc-header-search:hover {
			border-color: var(--mdc-header-search-border-hover-color);
		}

		.mdc-header-search--focused {
			box-shadow: 0 0 0 1px var(--mdc-header-search-focus-color);
			border-color: var(--mdc-header-search-focus-color) !important;
		}

		.mdc-header-search__input {
			display: flex;
			flex: 1;
		}

		.mdc-header-search__input input {
			width: 100%;
			height: auto;
			margin: 0;
			border: none;
			padding: 0.75rem 0 0.75rem 1rem;
			background: transparent;
			outline: none;
			appearance: none;
			font-family: var(--mdc-header-search-input-font-family);
			font-size: var(--mdc-header-search-input-font-size);
			color: var(--mdc-header-search-input-color);
			font-weight: 450;
			line-height: 1.3;
			text-align: right;
		}

		.mdc-header-search__input input::placeholder {
			font-family: var(--mdc-header-search-input-font-family);
			font-size: var(--mdc-header-search-input-font-size);
			color: var(--mdc-header-search-placeholder-color);
			font-weight: 450;
			line-height: 1.3;
			text-align: right;
		}

		.mdc-header-search__button {
			display: flex;
			align-items: center;
			justify-content: center;
			flex-shrink: 0;
			cursor: pointer;
			margin: 0 0 0 auto;
			border: none;
			padding: 0;
			background-color: transparent;
			appearance: none;
		}

		.mdc-header-search__button svg {
			width: var(--mdc-header-search__icon-size);
			height: var(--mdc-header-search__icon-size);
			padding-right: 1rem;
			padding-left: 1rem;
		}
	`

  get currentValue() {
    return this.value !== undefined && this.value !== '' ? this.value : this.internalValue
  }

  get currentFocused() {
    return this.focused !== undefined ? this.focused : this.isFocused
  }

  get currentHovered() {
    return this.hovered !== undefined ? this.hovered : this.isHovered
  }

  private handleChange(e: Event) {
    const input = e.target as HTMLInputElement
    const newValue = input.value
    if (this.value === undefined || this.value === '') {
      this.internalValue = newValue
    }
    this.dispatchEvent(
      new CustomEvent('change', {
        detail: { value: newValue },
        bubbles: true,
        composed: true,
      })
    )
  }

  private handleSubmit(e: Event) {
    e.preventDefault()
    this.dispatchEvent(
      new CustomEvent('submit', {
        detail: { value: this.currentValue },
        bubbles: true,
        composed: true,
      })
    )
  }

  private handleFocus() {
    this.isFocused = true
  }

  private handleBlur() {
    this.isFocused = false
  }

  private handleMouseEnter() {
    this.isHovered = true
  }

  private handleMouseLeave() {
    this.isHovered = false
  }

  render() {
    const className = `mdc-header-search${this.currentFocused ? ' mdc-header-search--focused' : ''}${this.currentHovered ? ' mdc-header-search--hovered' : ''}`

    return html`
			<form class=${className} @submit=${this.handleSubmit} @mouseenter=${this.handleMouseEnter} @mouseleave=${this.handleMouseLeave}>
				<div class="mdc-header-search__input">
					<input
						type="text"
						placeholder=${this.placeholder}
						.value=${this.currentValue}
						@input=${this.handleChange}
						@focus=${this.handleFocus}
						@blur=${this.handleBlur}
					/>
				</div>
				<button type="submit" class="mdc-header-search__button">
					<slot name="search-icon">
						<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
							<path
								d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13Z"
								stroke="currentColor"
								stroke-width="1.5"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<path d="M11.5 11.5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
						</svg>
					</slot>
				</button>
			</form>
		`
  }
}
